import type { ReactNode } from 'react';
import clsx from 'clsx';
import { faDiamondExclamation } from '@fortawesome/pro-solid-svg-icons/faDiamondExclamation';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import type { RequiredTextValue } from 'ui-components/components/text/text-types';

import style from './packing-inline-banner.module.css';

const STATUS_ICONS = {
  warning: faTriangleExclamation,
  error: faDiamondExclamation,
} as const;

export type PackingInlineBannerStatus = keyof typeof STATUS_ICONS;

type PackingInlineBannerProps = {
  status: PackingInlineBannerStatus;
  description: RequiredTextValue;
  title?: RequiredTextValue;
  actions?: ReactNode;
};

export const PackingInlineBanner = ({ status, description, title, actions }: PackingInlineBannerProps) => {
  const isStacked = Boolean(actions);

  return (
    <FlexRow className={clsx(style.banner, style.bannerRow, style[status], { [style.stacked]: isStacked })} gap={Gap.Space8}>
      <FontAwesomeIcon className={style.icon} icon={STATUS_ICONS[status]} />
      {isStacked ? (
        <FlexColumn className={style.content} gap={Gap.Space8}>
          <FlexColumn gap={Gap.Space8}>
            {title && <Text className={style.title} textType={TextType.Paragraph} value={title} />}
            <Text textType={TextType.Paragraph} value={description} />
          </FlexColumn>
          <FlexRow className={style.actionsRow} gap={Gap.Space8}>
            {actions}
          </FlexRow>
        </FlexColumn>
      ) : (
        <FlexRow className={clsx(style.textRow, style.textRowAlign)} gap={Gap.Space8}>
          {title && <Text className={style.title} textType={TextType.Paragraph} value={title} />}
          <Text className={style.description} textType={TextType.Paragraph} value={description} />
        </FlexRow>
      )}
    </FlexRow>
  );
};
